import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Stack,
  Badge,
  Flex,
  HStack,
  Icon,
  Card,
  CardBody,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
} from "@chakra-ui/react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { getDriverDetails, getDriverTrips, getDriverAuditLogs, getDriverFleets } from "../../../actions/inventory/driver.action";
import { DocumentCard } from "../../../components/DocumentImageViewer";
import { Loading } from "../../../components/Loading";
import { FaCalendarAlt } from "react-icons/fa";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import {
  DRIVER_OPERATIONAL_STATUS,
  DRIVER_OPERATIONAL_STATUS_COLOR,
  DRIVER_OPERATIONAL_STATUS_LABEL,
} from "../../../constants/driverStatus";

const SERVER = import.meta.env.VITE_API_URL || "https://api.travelprym.in";

const DriverFleets = ({ driverId }) => {
  const [fleets, setFleets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFleets = async () => {
      try {
        const res = await getDriverFleets(driverId);
        setFleets(res?.fleets || []);
      } catch (err) {
        console.error("Failed to fetch fleets", err);
      } finally {
        setLoading(false);
      }
    };
    if (driverId) fetchFleets();
  }, [driverId]);

  if (loading) return <Spinner />;
  if (fleets.length === 0) return <Text color="gray.500">No vehicles found.</Text>;

  return (
    <Box overflowX="auto" w="full">
      <Table variant="simple" size="sm" w="full">
        <Thead>
          <Tr>
            <Th>Vehicle No.</Th>
            <Th>Model</Th>
            <Th>Status</Th>
          </Tr>
        </Thead>
        <Tbody>
          {fleets.map((fleet) => (
            <Tr key={fleet._id}>
              <Td>
                <Text as={RouterLink} to={"/fleet-management/vehicles/" + fleet._id} color="blue.500" fontWeight="medium">
                  {fleet.vehicleNumber}
                </Text>
              </Td>
              <Td>{fleet.model}</Td>
              <Td>{fleet.status?.operationalStatus}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

const DriverTrips = ({ driverId }) => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const res = await getDriverTrips(driverId);
        setTrips(res?.trips || res?.data?.trips || []);
      } catch (err) {
        console.error("Failed to fetch trips", err);
      } finally {
        setLoading(false);
      }
    };
    if (driverId) fetchTrips();
  }, [driverId]);

  if (loading) return <Spinner />;

  if (trips.length === 0) {
    return <Text color="gray.500">No trips found for this driver.</Text>;
  }

  return (
    <Box overflowX="auto" w="full">
      <Table variant="simple" size="sm" w="full">
        <Thead>
          <Tr>
            <Th>Trip Code</Th>
            <Th>Booking Status</Th>
            <Th>Trip Status</Th>
            <Th>Vendor</Th>
            <Th>Vehicle</Th>
            <Th>Date</Th>
          </Tr>
        </Thead>
        <Tbody>
          {trips.map((trip) => (
            <Tr key={trip._id} _hover={{ bg: "gray.50" }}>
              <Td>
                <Text
                  as={RouterLink}
                  to={`/bookings/${trip.bookingId?._id || trip.bookingId}`}
                  color="blue.500"
                  fontWeight="medium"
                >
                  {trip.tripCode || trip._id.substring(0, 8)}
                </Text>
              </Td>
              <Td>
                <Badge
                  colorScheme={
                    trip.bookingStatus === "completed"
                      ? "green"
                      : trip.bookingStatus === "cancelled"
                        ? "red"
                        : "yellow"
                  }
                >
                  {trip.bookingStatus}
                </Badge>
              </Td>
              <Td>
                <Badge
                  colorScheme={
                    trip.tripStatus === "completed"
                      ? "green"
                      : trip.tripStatus === "in_progress"
                        ? "blue"
                        : "gray"
                  }
                >
                  {trip.tripStatus}
                </Badge>
              </Td>
              <Td>{trip.vendorId?.vendorName || "-"}</Td>
              <Td>{trip.fleetId?.vehicleNumber || "-"}</Td>
              <Td>{new Date(trip.createdAt).toLocaleDateString()}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

const DriverAuditLogs = ({ driverId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const res = await getDriverAuditLogs(driverId);
        setLogs(res?.data || []);
      } catch (err) {
        console.error("Failed to fetch audit logs", err);
      } finally {
        setLoading(false);
      }
    };
    if (driverId) fetchLogs();
  }, [driverId]);

  if (loading) return <Spinner />;

  if (logs.length === 0) {
    return <Text color="gray.500">No audit logs found.</Text>;
  }

  return (
    <Box overflowX="auto" w="full">
      <Table variant="simple" size="sm" w="full">
        <Thead>
          <Tr>
            <Th>Date & Time</Th>
            <Th>Action</Th>
            <Th>Performed By</Th>
            <Th>Role</Th>
            <Th>Changes Summary</Th>
          </Tr>
        </Thead>
        <Tbody>
          {logs.map((log) => (
            <Tr key={log._id} _hover={{ bg: "gray.50" }}>
              <Td whiteSpace="nowrap">
                {new Date(log.createdAt).toLocaleString()}
              </Td>
              <Td>
                <Badge
                  colorScheme={
                    log.actionType === "CREATE"
                      ? "green"
                      : log.actionType === "UPDATE"
                        ? "blue"
                        : "red"
                  }
                >
                  {log.actionType}
                </Badge>
              </Td>
              <Td>{log.performedByInfo?.name || "-"}</Td>
              <Td>{log.performedByInfo?.role || "-"}</Td>
              <Td maxW="300px">
                <Text noOfLines={2} title={log.changesDescription}>
                  {log.changesDescription || "No specific details"}
                </Text>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

const DriverEach = () => {
  const { id } = useParams();
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);

  const DriverDetails = async () => {
    try {
      const res = await getDriverDetails(id);
      setDriver(res?.data?.data || res?.data || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const InfoItem = ({ label, value, icon }) => (
    <Box>
      <HStack spacing={2} mb={1}>
        {icon && <Icon as={icon} color="blue.500" boxSize={4} />}
        <Text fontSize="sm" color="gray.500" fontWeight="medium">
          {label}
        </Text>
      </HStack>
      <Text fontWeight="semibold" fontSize="md">
        {value || "-"}
      </Text>
    </Box>
  );

  const ImageCard = ({ label, fileName }) => {
    return (
      <Box
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        p={3}
        w="100%"
      >
        <Text fontSize="sm" color="gray.500" mb={1}>
          {label}
        </Text>
        <Text fontSize="sm" fontWeight="medium">
          {fileName || "-"}
        </Text>
      </Box>
    );
  };

  const CertificateCard = ({ label, imageUrl }) => (
    <DocumentCard label={label} imageUrl={imageUrl} />
  );

  useEffect(() => {
    DriverDetails();
  }, [id]);

  if (loading) {
    return <Loading />;
  }

  if (!driver) {
    return (
      <Box textAlign="center" p={10}>
        <Text fontSize="xl" color="gray.600">
          No driver data found
        </Text>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }} maxW="1200px" mx="auto" w="100%">
      {/* Breadcrumbs */}
      <BreadCrumbMaker
        items={[
          { title: "Fleet Management", link: "#" },
          { title: "Drivers", link: "/fleet-management/driver" },
          { title: "View Driver", link: `/fleet-management/driver/${id}`, on: true },
        ]}
      />

      <Tabs colorScheme="blue" variant="unstyled" mt={4}>
        <TabList
          overflowX="auto"
          sx={{
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          <Tab
            fontWeight="medium"
            border="1px solid"
            borderColor="#396AFF"
            color="#396AFF"
            _selected={{ color: "white", bg: "#396AFF" }}
            mr={2}
            borderRadius="md"
            whiteSpace="nowrap"
            flexShrink={0}
          >
            Documents
          </Tab>
          <Tab
            fontWeight="medium"
            border="1px solid"
            borderColor="#396AFF"
            color="#396AFF"
            _selected={{ color: "white", bg: "#396AFF" }}
            borderRadius="md"
            mr={2}
            whiteSpace="nowrap"
            flexShrink={0}
          >
            Trips
          </Tab>
          {driver?.driverType === "driver_with_vehicle" && (
            <Tab
              fontWeight="medium"
              border="1px solid"
              borderColor="#396AFF"
              color="#396AFF"
              _selected={{ color: "white", bg: "#396AFF" }}
              borderRadius="md"
              mr={2}
              whiteSpace="nowrap"
              flexShrink={0}
            >
              Vehicles
            </Tab>
          )}
          <Tab
            fontWeight="medium"
            border="1px solid"
            borderColor="#396AFF"
            color="#396AFF"
            _selected={{ color: "white", bg: "#396AFF" }}
            borderRadius="md"
            whiteSpace="nowrap"
            flexShrink={0}
          >
            Audit Log
          </Tab>
        </TabList>
        <TabPanels>
          {/* Documents Tab */}
          <TabPanel px={0} pt={6}>
            <Card borderRadius="xl" boxShadow="sm" mb={6}>
              <CardBody p={{ base: 4, md: 6 }}>
                <Heading size="sm" color="gray.700" mb={6}>
                  Driver Information
                </Heading>

                <SimpleGrid
                  columns={{ base: 1, md: 4 }}
                  spacing={{ base: 4, md: 6 }}
                  mb={6}
                >
                  <InfoItem label="Driver Unique ID" value={driver.uniqueDriverId} />
                  <InfoItem label="Driver Name" value={driver.name} />
                  <InfoItem label="Mobile Number" value={driver.mobileNumber} />
                  <InfoItem label="WhatsApp Number" value={driver.whatsappNumber} />
                  <InfoItem label="Driver Email Id" value={driver.email} />
                  <InfoItem
                    label="Driver Type"
                    value={
                      driver.driverType === "vendor_driver"
                        ? "Vendor Driver"
                        : "Driver with Vehicle"
                    }
                  />
                  <Box>
                    <Text fontSize="sm" color="gray.500" fontWeight="medium" mb={1}>
                      Vendor Relation
                    </Text>
                    {driver.vendorId && driver.vendorName ? (
                      <Text
                        as={RouterLink}
                        to={`/fleet-management/vendor/${driver.vendorId}`}
                        color="blue.500"
                        fontWeight="semibold"
                        fontSize="md"
                        textDecoration="underline"
                      >
                        {driver.vendorName}
                      </Text>
                    ) : (
                      <Text fontWeight="semibold" fontSize="md">
                        Self
                      </Text>
                    )}
                  </Box>
                </SimpleGrid>

                <SimpleGrid
                  columns={{ base: 1, md: 4 }}
                  spacing={{ base: 4, md: 6 }}
                  mb={6}
                >
                  <InfoItem
                    label="Date Of Birth"
                    value={
                      driver.dob ? new Date(driver.dob).toLocaleDateString() : "-"
                    }
                  />
                  <InfoItem label="State" value={driver.state} />
                  <InfoItem label="City" value={driver.city} />
                  <InfoItem label="Address" value={driver.address} />
                  <InfoItem label="Aadhaar Number" value={driver.aadhaarCardNumber} />
                  <InfoItem label="PAN Number" value={driver.panCardNumber} />
                  <InfoItem
                    label="License Number"
                    value={driver.drivingLicenseNumber}
                  />
                </SimpleGrid>

                <SimpleGrid
                  columns={{ base: 1, md: 4 }}
                  spacing={{ base: 4, md: 6 }}
                  mb={6}
                >
                  <CertificateCard
                    label="Aadhaar Card Front Image"
                    imageUrl={driver.aadhaarCardFront}
                  />
                  <CertificateCard
                    label="Aadhaar Card Back Image"
                    imageUrl={driver.aadhaarCardBack}
                  />
                  <CertificateCard
                    label="Pancard Front Image"
                    imageUrl={driver.panCardFront}
                  />
                  <CertificateCard
                    label="Driving License Front"
                    imageUrl={driver.drivingLicenseFront}
                  />
                  <CertificateCard
                    label="Driving License Back"
                    imageUrl={driver.drivingLicenseBack}
                  />
                </SimpleGrid>

                <Heading size="sm" color="gray.700" mb={6} mt={6}>
                  Driver Status
                </Heading>

                <SimpleGrid
                  columns={{ base: 1, md: 4 }}
                  spacing={{ base: 4, md: 6 }}
                  mb={6}
                >
                  <Box>
                    <Text fontSize="sm" color="gray.500" fontWeight="medium" mb={1}>
                      Operational Status
                    </Text>
                    <Badge
                      colorScheme={
                        DRIVER_OPERATIONAL_STATUS_COLOR[driver.operationalStatus] || "gray"
                      }
                      px={3}
                      py={1}
                      rounded="lg"
                      fontSize="sm"
                    >
                      {DRIVER_OPERATIONAL_STATUS_LABEL[
                        driver.operationalStatus || DRIVER_OPERATIONAL_STATUS.UNASSIGNED
                      ]}
                    </Badge>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500" fontWeight="medium" mb={1}>
                      Account Status
                    </Text>
                    <Badge
                      colorScheme={
                        driver.accountStatus === "active" ? "green" :
                        driver.accountStatus === "inactive" ? "gray" :
                        driver.accountStatus === "suspended" ? "orange" :
                        driver.accountStatus === "blacklisted" ? "red" : "gray"
                      }
                      px={3}
                      py={1}
                      rounded="lg"
                      fontSize="sm"
                    >
                      {driver.accountStatus ? driver.accountStatus.charAt(0).toUpperCase() + driver.accountStatus.slice(1) : "Active"}
                    </Badge>
                  </Box>
                </SimpleGrid>
              </CardBody>
            </Card>
          </TabPanel>
          {/* Trips Tab */}
          <TabPanel px={0} pt={6}>
            <Card borderRadius="xl" boxShadow="sm" w="full">
              <CardBody>
                <Heading size="sm" color="gray.700" mb={6}>
                  Driver Trips
                </Heading>
                <DriverTrips driverId={driver._id || id} />
              </CardBody>
            </Card>
          </TabPanel>
          {/* Vehicles Tab (Conditional) */}
          {driver?.driverType === "driver_with_vehicle" && (
            <TabPanel px={0} pt={6}>
              <Card borderRadius="xl" boxShadow="sm" w="full">
                <CardBody>
                  <Heading size="sm" color="gray.700" mb={6}>
                    Attached Vehicles
                  </Heading>
                  <DriverFleets driverId={driver._id || id} />
                </CardBody>
              </Card>
            </TabPanel>
          )}
          {/* Audit Log Tab */}
          <TabPanel px={0} pt={6}>
            <Card borderRadius="xl" boxShadow="sm" w="full">
              <CardBody>
                <Heading size="sm" color="gray.700" mb={6}>
                  Audit Log
                </Heading>
                <DriverAuditLogs driverId={driver._id || id} />
              </CardBody>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default DriverEach;
